/** localStorage key of the "always refresh automatically" switch. */
export const AUTO_REFRESH_KEY = 'kendrick-auto-refresh'

/** Shared state for the "new version available" notice (site and back-office). */
export const useAppUpdateState = () => useState('app-update', () => ({ available: false, latestId: '' }))

/** Number of screens currently in edit mode: a pending update must not reload them. */
const useEditingCount = () => useState('app-editing-count', () => 0)

/**
 * Marks a screen as "in edit mode" while `active` is true (open form, unsaved
 * changes), so the automatic page update waits for the user to finish.
 */
export const useEditingGuard = (active: Ref<boolean> | ComputedRef<boolean>) => {
  const count = useEditingCount()
  let held = false
  const sync = (value: boolean) => {
    if (value && !held) { held = true; count.value += 1 }
    else if (!value && held) { held = false; count.value = Math.max(0, count.value - 1) }
  }
  watch(active, sync, { immediate: true })
  onBeforeUnmount(() => sync(false))
}

// Typing in a real form (not a search box) since the last navigation or save.
let formDirty = false
export const markFormDirty = (value: boolean) => { formDirty = value }

/** True while the user is typing, has a form open or has unsaved form input. */
export const isUserEditing = () => {
  if (typeof document === 'undefined') return false
  if (useEditingCount().value > 0 || formDirty) return true
  const el = document.activeElement as HTMLElement | null
  if (!el) return false
  return el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable ||
    (el.tagName === 'INPUT' && !['button', 'submit', 'checkbox', 'radio', 'search'].includes((el as HTMLInputElement).type))
}
